<template>
  <sdPageHeader title="Approval Pembinaan Person Grade"> </sdPageHeader>
  <Main>
    <sdCards headless>
      <!-- Search Section -->
      <div class="search-section">
        <a-input-search
          v-model="searchQuery"
          placeholder="Cari Sesuatu..."
          style="width: 250px"
          @search="handleSearch"
        />
      </div>

      <!-- Table Section -->
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="{
          total: pagination.total,
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: pagination.showSizeChanger,
          pageSizeOptions: pagination.pageSizeOptions,
          showTotal: pagination.showTotal,
        }"
        @change="handleTableChange"
      >
        <template #status="{ text }">
          <a-tag :color="getStatusColor(text)">
            {{ text }}
          </a-tag>
        </template>
        <template #action="{ record }">
          <div class="action-buttons">
            <a-button type="link" shape="circle" @click="viewDetails(record)">
              <template #icon><eye-outlined /></template>
            </a-button>
          </div>
        </template>
      </a-table>

      <!-- No approval button or confirmation modal needed -->
    </sdCards>
  </Main>
</template>

<script>
import { Main } from '../styled';
import { EyeOutlined } from '@ant-design/icons-vue';
import assessmentApi from '@/api/assessment';
import { getItem } from '@/utility/localStorageControl';
import { handleSessionExpired } from '@/utility/utility';

const ViewPage = {
  name: 'AppList',
  components: {
    Main,
    EyeOutlined,
  },
  data() {
    return {
      searchQuery: '',
      loading: false,
      statusFilters: [],
      columns: [
        {
          title: 'No',
          dataIndex: 'no',
          key: 'no',
          width: 70,
        },
        {
          title: 'NIP',
          dataIndex: 'nip',
          key: 'nip',
        },
        {
          title: 'Nama Pegawai',
          dataIndex: 'namaPegawai',
          key: 'namaPegawai',
        },
        {
          title: 'PeG',
          dataIndex: 'peg',
          key: 'peg',
          width: 80,
        },
        {
          title: 'Jabatan',
          dataIndex: 'jabatan',
          key: 'jabatan',
        },
        {
          title: 'PoG Jabatan Existing',
          dataIndex: 'pog',
          key: 'pog',
        },
        {
          title: 'Proyeksi Jabatan',
          dataIndex: 'proyeksiJabatan',
          key: 'proyeksiJabatan',
        },
        // {
        //   title: 'Personal Area',
        //   dataIndex: 'personalArea',
        //   key: 'personalArea',
        // },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          slots: { customRender: 'status' },
          filters: [
            { text: 'Inisiasi Pengajuan', value: 'Inisiasi Pengajuan' },
            { text: 'Disetujui', value: 'Disetujui' },
            { text: 'Ditinjau Ulang', value: 'Ditinjau Ulang' },
            { text: 'Menunggu Approval Atasan', value: 'Menunggu Approval Atasan' },
            { text: 'Menunggu Approval BOD-1', value: 'Menunggu Approval BOD-1' },
            { text: 'Menunggu Approval HC', value: 'Menunggu Approval HC' },
            { text: 'Disetujui Oleh HC', value: 'Disetujui Oleh HC' },
            { text: 'Ditinjau Ulang Oleh HC', value: 'Ditinjau Ulang Oleh HC' },
          ],
          onFilter: (value, record) => record.status === value,
        },
        {
          title: 'Aksi',
          key: 'action',
          width: 80,
          slots: { customRender: 'action' },
        },
      ],
      tableData: [],
      pagination: {
        total: 1,
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total, range) => `Menampilkan ${range[0]}-${range[1]} dari ${total} Data`,
        onChange: () => {}, // Empty function to be filled by handleTableChange
        onShowSizeChange: () => {}, // Empty function to be filled by handleTableChange
      },
    };
  },
  created() {
    // Fetch data when component is created
    this.fetchTableData();
  },
  methods: {
    fetchTableData() {
      // Show loading state
      this.loading = true;

      // Get access token from localStorage
      const accessToken = getItem('access_token');

      // Set headers with the token
      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      // Prepare query parameters for pagination
      const params = {
        page: this.pagination.current,
        per_page: this.pagination.pageSize,
      };

      // Add search query if present - specifically for employee name
      if (this.searchQuery) {
        params.name = this.searchQuery.toUpperCase();
      }

      // Add status filters if any are selected
      if (this.statusFilters && this.statusFilters.length > 0) {
        // Convert display status values back to API status values
        const apiStatusValues = this.statusFilters.map(this.mapDisplayStatusToApiStatus);
        params.status = apiStatusValues.join(',');
      }

      // Fetch data from the API - use approval endpoint
      assessmentApi
        .get(`my-pengajuan?with=proyeksiJabatan,userAssessment`, {
          headers,
          params,
        })
        .then(response => {
          if (response.data && response.data.data && response.data.data.data) {
            const apiData = response.data.data.data;

            // Map API data to table format
            let mappedData = apiData.map((item, index) => {
              const proyeksi = item.proyeksi_jabatan || {};
              const userAssessment = item.user_assessment || {};
              return {
                key: item.id.toString(),
                no: index + 1,
                nip: item.nip || '',
                namaPegawai: userAssessment.name || '',
                peg: item.peg || '',
                jabatan: item.jabatan || '',
                // pog: proyeksi.pog ? proyeksi.pog.toString() : '',
                pog: item.peg || '',
                personalArea: userAssessment.personal_area || '',
                // proyeksiJabatan: proyeksi.jabatan || '',
                proyeksiJabatan: (proyeksi.pog ? proyeksi.pog + ' - ' : '') + (proyeksi.jabatan || ''),
                status: this.formatStatus(item.status),
                record: item, // Store the full record for reference
              };
            });

            // Set the table data directly without client-side filtering
            // since we're now handling filtering on the server side
            this.tableData = mappedData;

            // Update pagination
            const paginationInfo = response.data.data;
            this.pagination = {
              ...this.pagination,
              total: paginationInfo.total || 0,
              current: paginationInfo.current_page || 1,
              pageSize: paginationInfo.per_page || 10,
              // Preserve the callback functions
              onChange: page => {
                this.pagination.current = page;
                this.fetchTableData();
              },
              onShowSizeChange: (current, size) => {
                this.pagination.current = current;
                this.pagination.pageSize = size;
                this.fetchTableData();
              },
            };
          }
        })
        .catch(error => {
          console.error('Error fetching approval data:', error);
          this.$message.error('Gagal memuat data pengajuan untuk approval');
          if (error.response && error.response.status === 401) {
            handleSessionExpired();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatStatus(status) {
      // Format the status for display
      switch (status) {
        case 'inisialisasi':
          return 'Inisiasi Pengajuan';
        case 'disetujui':
          return 'Disetujui';
        case 'ditolak':
          return 'Ditinjau Ulang';
        case 'menunggu_approval_atasan':
          return 'Menunggu Approval Atasan';
        case 'menunggu_approval_bod1':
          return 'Menunggu Approval BOD-1';
        case 'menunggu_approval_hc':
          return 'Menunggu Approval HC';
        case 'disetujui_hc':
          return 'Disetujui Oleh HC';
        case 'ditolak_hc':
          return 'Ditinjau Ulang Oleh HC';
        default:
          return status || 'Unknown';
      }
    },

    getStatusColor(status) {
      // Customize the color based on different statuses
      switch (status) {
        case 'Inisiasi Pengajuan':
          return 'blue';
        case 'Disetujui':
          return 'green';
        case 'Ditinjau Ulang':
          return 'red';
        case 'Menunggu Approval Atasan':
          return 'orange';
        case 'Menunggu Approval BOD-1':
          return 'orange';
        case 'Menunggu Approval HC':
          return 'orange';
        case 'Disetujui Oleh HC':
          return 'green';
        case 'Ditinjau Ulang Oleh HC':
          return 'red';
        default:
          return 'default';
      }
    },

    handleSearch(value) {
      console.log('Searching for nama pegawai:', value);
      // Update search query to filter by employee name
      this.searchQuery = value;
      this.fetchTableData();
    },
    handleTableChange(pagination, filters) {
      console.log('Table params changed:', { pagination, filters });
      // Update pagination state
      this.pagination = {
        ...this.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        // Update the callback functions
        onChange: page => {
          this.pagination.current = page;
          this.fetchTableData();
        },
        onShowSizeChange: (current, size) => {
          this.pagination.current = current;
          this.pagination.pageSize = size;
          this.fetchTableData();
        },
      };
      // Store status filters
      this.statusFilters = filters.status || [];
      // Fetch data with updated pagination and filters
      this.fetchTableData();
    },
    viewDetails(record) {
      console.log('Viewing details for:', record);
      // In a real app, you would pass the record ID
      this.$router.push({ name: 'monitoring', params: { id: record.key } });
    },

    mapDisplayStatusToApiStatus(displayStatus) {
      switch (displayStatus) {
        case 'Inisiasi Pengajuan':
          return 'inisialisasi';
        case 'Disetujui':
          return 'disetujui';
        case 'Ditinjau Ulang':
          return 'ditolak';
        case 'Menunggu Approval Atasan':
          return 'menunggu_approval_atasan';
        case 'Menunggu Approval BOD-1':
          return 'menunggu_approval_bod1';
        case 'Menunggu Approval HC':
          return 'menunggu_approval_hc';
        case 'Disetujui Oleh HC':
          return 'disetujui_hc';
        case 'Ditinjau Ulang Oleh HC':
          return 'ditolak_hc';
        default:
          return displayStatus;
      }
    },
  },
};

export default ViewPage;
</script>

<style scoped>
.search-section {
  margin-bottom: 20px;
}

.submit-section {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.confirmation-modal {
  .confirmation-content {
    padding: 0 16px;
  }

  p {
    margin-bottom: 24px;
    line-height: 1.6;
    text-align: center;
  }

  .confirmation-buttons {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    padding: 16px 0;
  }
}
</style>
